import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { Check, Search, Users } from 'lucide-react'

import { IconButton } from '../../app/components/ui/IconButton'
import { Popover, PopoverContent, PopoverTrigger } from '../../app/components/ui/popover'
import { fetchClients } from '../../app/api/explorer'
import type { ClientsListPayload, ClientsListRow } from '../../app/contracts'
import { clientRefFromRow, type ClientRef } from './types'
import styles from './PickerMenu.module.scss'

interface Props {
  selectedClientCode: string | null
  onSelect: (client: ClientRef) => void
  disabled?: boolean
}

type FetchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; rows: ClientsListRow[] }
  | { status: 'error'; message: string }

const VISIBLE_LIMIT = 50

function matches(row: ClientsListRow, query: string): boolean {
  if (!query) return true
  const haystack = `${row.client_code} ${row.client_label}`.toLowerCase()
  return haystack.includes(query)
}

export function ClientPickerMenu({ selectedClientCode, onSelect, disabled }: Props) {
  const [open, setOpen] = useState(false)
  const [state, setState] = useState<FetchState>({ status: 'idle' })
  const [query, setQuery] = useState('')
  const listboxId = useId()
  const stateRef = useRef(state)
  stateRef.current = state
  const abortRef = useRef<AbortController | null>(null)

  const loadClients = useCallback(() => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    setState({ status: 'loading' })
    fetchClients('', controller.signal)
      .then((payload: ClientsListPayload) => {
        if (controller.signal.aborted) return
        setState({ status: 'ready', rows: payload.rows })
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return
        const message = err instanceof Error ? err.message : 'Errore nel caricamento clienti'
        setState({ status: 'error', message })
      })
  }, [])

  useEffect(() => () => abortRef.current?.abort(), [])

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next)
      if (next) {
        const status = stateRef.current.status
        if (status === 'idle' || status === 'error') loadClients()
      } else {
        setQuery('')
      }
    },
    [loadClients],
  )

  const normalizedQuery = query.trim().toLowerCase()

  const filtered = useMemo<ClientsListRow[]>(() => {
    if (state.status !== 'ready') return []
    if (!normalizedQuery) return state.rows.slice(0, VISIBLE_LIMIT)
    const matched: ClientsListRow[] = []
    for (const row of state.rows) {
      if (matches(row, normalizedQuery)) matched.push(row)
      if (matched.length >= VISIBLE_LIMIT) break
    }
    return matched
  }, [state, normalizedQuery])

  const totalAvailable = state.status === 'ready' ? state.rows.length : 0
  const filteredCount = filtered.length

  const handlePick = (row: ClientsListRow) => {
    onSelect(clientRefFromRow(row))
    // FTUE-side signal: lets the cold-list flow advance when the
    // banker picks a client from this popup. Kept separate from the
    // existing CLIENT_CODE_SELECTED_EVENT (which already wires up via
    // setClient in the surrounding chat container) so we don't trigger
    // a redundant chatContext.setClient call on this code path.
    if (typeof document !== 'undefined') {
      document.dispatchEvent(new CustomEvent('credem:ftue-clienti-popup-pick'))
    }
    setOpen(false)
  }

  const handleInteractOutside = useCallback((event: Event) => {
    if (typeof document === 'undefined') return
    const tokens = (document.body.dataset.ftueLock ?? '').split(/\s+/)
    if (tokens.includes('clienti-popup-no-dismiss')) {
      event.preventDefault()
    }
  }, [])

  const handleRetry = () => {
    loadClients()
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <IconButton
          size="md"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-label="Aggiungi cliente al contesto"
          disabled={disabled}
          title="Aggiungi cliente al contesto"
          data-ftue-step="chat-clienti-button"
        >
          <Users aria-hidden />
        </IconButton>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        sideOffset={8}
        className={styles.popover}
        data-ftue-step="clienti-popup"
        onInteractOutside={handleInteractOutside}
        onEscapeKeyDown={handleInteractOutside}
      >
        <div className={styles.searchRow}>
          <Search aria-hidden />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca per codice o nome"
            className={styles.searchInput}
            aria-label="Cerca cliente"
            aria-controls={listboxId}
          />
        </div>

        {state.status === 'loading' ? (
          <div className={styles.stateRow} role="status">
            Caricamento clienti...
          </div>
        ) : null}

        {state.status === 'error' ? (
          <div className={[styles.stateRow, styles.stateError].join(' ')} role="alert">
            {state.message}
            <button type="button" className={styles.retryButton} onClick={handleRetry}>
              Riprova
            </button>
          </div>
        ) : null}

        {state.status === 'ready' ? (
          totalAvailable === 0 ? (
            <div className={styles.stateRow} role="status">
              Nessun cliente nel tuo portafoglio.
            </div>
          ) : filteredCount === 0 ? (
            <div className={styles.stateRow} role="status">
              Nessun cliente corrisponde a “{query}”.
            </div>
          ) : (
            <>
              <ul
                id={listboxId}
                role="listbox"
                aria-label="Clienti"
                aria-activedescendant={
                  selectedClientCode ? `${listboxId}-${selectedClientCode}` : undefined
                }
                className={styles.list}
              >
                {filtered.map((row) => {
                  const selected = row.client_code === selectedClientCode
                  return (
                    <li key={row.client_code} role="presentation">
                      <button
                        type="button"
                        id={`${listboxId}-${row.client_code}`}
                        role="option"
                        aria-selected={selected}
                        className={[styles.listItem, selected ? styles.listItemSelected : '']
                          .filter(Boolean)
                          .join(' ')}
                        onClick={() => handlePick(row)}
                      >
                        <span className={styles.listItemBody}>
                          <span className={styles.listItemTitle}>{row.client_label}</span>
                          <span className={styles.listItemMeta}>
                            {row.client_code}
                            {row.segment_name ? ` · ${row.segment_name}` : ''}
                          </span>
                        </span>
                        {selected ? (
                          <Check className={styles.checkboxIcon} aria-hidden />
                        ) : null}
                      </button>
                    </li>
                  )
                })}
              </ul>
              <div className={styles.liveRegion} aria-live="polite">
                {filteredCount} di {totalAvailable} clienti
              </div>
            </>
          )
        ) : null}
      </PopoverContent>
    </Popover>
  )
}
